import { useRef } from "react";

import { SyntheticEvent } from "react";

import { useTranslator } from "@eo-locale/react";

import { useSearchParams } from "next/navigation";

import useVerificationCode from "@/hooks/useVerificationCode";
import { extractQueryParams } from "@/helpers/extract-query-params";

import { Box, Button, Alert, useToast } from "@/storybook-components";
import type { Question } from "@/types";
import QuestionFactory from "../questions/question-factory";
import RequiredField from "../questions/required-field";

import { setError, setStatus } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { saveRegistrations } from "@/redux/features/registrations/thunk";
import { setClientRecaptchaValidation } from "@/redux/features/recaptcha/slice";

import {
  validateQuestion,
  reduceQuestionsToFlattenedTrees
} from "@/helpers/questions";
import InvisibleReCaptcha from "../recaptcha/invisible";
import { highlightError } from "@/helpers/highlight-error";
import usePreview from "@/hooks/usePreview";

const paramMappings = {
  type: "type",
  token: "token"
};

const Form = (): JSX.Element => {
  const toast = useToast();
  const { isPreview } = usePreview();
  const recaptchaRef = useRef(null);
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const { answers } = useAppSelector((state) => state.questions);
  const { settings } = useAppSelector((state) => state.settings);
  const { status, error } = useAppSelector((state) => state.registrations);
  const { signupFormQuestions } = useAppSelector(
    (state) => state.signupFormQuestions
  );
  const { registeredUser } = useAppSelector((state) => state.user);
  const isVerificationCode = useVerificationCode();

  const { recaptchaSignupEnabled } = settings;
  const isReCaptchaEnabled =
    recaptchaSignupEnabled && process.env.RECAPTCHA_INVISIBLE_SITE_KEY;

  const questions = reduceQuestionsToFlattenedTrees(
    signupFormQuestions,
    answers
  );
  const user = registeredUser?.profile?.login || null;

  const handleSubmit = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();
    dispatch(setStatus("saving"));
    let isError = false;

    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];

      if (
        isVerificationCode &&
        (question.type === "SignupForm::AccountEmailQuestion" ||
          question.type === "SignupForm::LoginQuestion")
      ) {
        continue;
      }

      const error = await validateQuestion(answers[question.id], question);

      if (error?.length) {
        isError = true;
        dispatch(setStatus("idle"));
        dispatch(setError({ questionId: question.id, error }));
      }
    }

    if (document.querySelector(".ehqQuestionError")) {
      dispatch(setStatus("idle"));
      return highlightError();
    }

    if (isReCaptchaEnabled) {
      const recaptchaToken = await recaptchaRef.current.executeAsync();
      dispatch(setClientRecaptchaValidation(recaptchaToken));
      recaptchaRef.current.reset();
    }

    if (isPreview) {
      toast({
        title: user ? `${translate("common.heyUser", { user })},` : null,
        description: translate("common.thankYouForPreview", {
          formType: "register"
        }),
        status: "success",
        duration: 6000,
        isClosable: true
      });
      dispatch(setStatus("idle"));

      return;
    }

    if (!isError) {
      const params = extractQueryParams(searchParams, paramMappings);
      await dispatch(saveRegistrations(params));
      dispatch(setStatus("idle"));
    }
  };

  return (
    <Box
      as="form"
      noValidate
      onSubmit={handleSubmit}
      autoComplete="off"
      data-testid="register-participant-form"
    >
      <RequiredField />
      {questions.map((question: Question) => (
        <QuestionFactory key={question.id} question={question} />
      ))}
      {isReCaptchaEnabled && <InvisibleReCaptcha recaptchaRef={recaptchaRef} />}
      {error && (
        <Alert.Root
          status="error"
          borderRadius="md"
          data-testid="register-participant-alert-save-error"
        >
          <Alert.Indicator />
          <Alert.Title>{translate("errors.saveError")}</Alert.Title>
        </Alert.Root>
      )}
      <Button
        variant="primary"
        my={3}
        size="lg"
        type="submit"
        width="full"
        loading={status === "saving"}
        data-testid="registerButton"
      >
        {translate("common.register")}
      </Button>
    </Box>
  );
};

export default Form;
